import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { offersAPI } from '../../services/api';
import Header from '../../components/layout/Header';
import { ArrowRight, TrendingUp } from 'lucide-react';

export default function Home() {
  const { data: featuredData, isLoading: featuredLoading } = useQuery({
    queryKey: ['offers', 'featured'],
    queryFn: () => offersAPI.getFeatured().then((res) => res.data),
  });

  const { data: trendingData } = useQuery({
    queryKey: ['offers', 'trending'],
    queryFn: () => offersAPI.getTrending().then((res) => res.data),
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Never Miss a Food Deal in Udaipur
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            All restaurant offers in one place, updated daily
          </p>
          <Link
            to="/offers"
            className="inline-flex items-center px-8 py-4 bg-white text-red-600 rounded-lg font-semibold hover:bg-gray-100 transition"
          >
            Browse All Offers
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Featured Offers */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Featured Offers</h2>
          <Link to="/offers?featured=true" className="text-red-600 hover:text-red-700 font-medium">
            View All →
          </Link>
        </div>

        {featuredLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow-md h-96 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredData?.data?.slice(0, 6).map((item: any) => (
              <Link
                key={item.offer.id}
                to={`/offers/${item.offer.id}`}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition group"
              >
                <div className="relative h-48 bg-gray-200">
                  {item.offer.imageUrl ? (
                    <img
                      src={item.offer.imageUrl}
                      alt={item.offer.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No Image
                    </div>
                  )}
                  {item.offer.discountPercentage && (
                    <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full font-bold">
                      {item.offer.discountPercentage}% OFF
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-600 mb-1">{item.restaurant?.name}</p>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {item.offer.title}
                  </h3>
                  <div className="flex items-center gap-2 mb-2">
                    {item.offer.offerPrice && (
                      <span className="text-2xl font-bold text-green-600">
                        ₹{item.offer.offerPrice}
                      </span>
                    )}
                    {item.offer.originalPrice && (
                      <span className="text-gray-400 line-through">₹{item.offer.originalPrice}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    Valid until {new Date(item.offer.validTo).toLocaleDateString()}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Trending Offers */}
      {trendingData?.data && trendingData.data.length > 0 && (
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 mb-8">
              <TrendingUp className="w-8 h-8 text-red-600" />
              <h2 className="text-3xl font-bold text-gray-900">Trending Offers</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {trendingData.data.slice(0, 4).map((item: any) => (
                <Link
                  key={item.offer.id}
                  to={`/offers/${item.offer.id}`}
                  className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition"
                >
                  <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                    {item.offer.title}
                  </h3>
                  <p className="text-sm text-gray-600">{item.restaurant?.name}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {item.offer.viewsCount} views
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Restaurant Owner?</h2>
          <p className="text-xl mb-8 opacity-90">
            List your offers for free and reach thousands of food lovers in Udaipur
          </p>
          <a
            href="mailto:hello@udaipurdeals.com"
            className="inline-flex items-center px-8 py-4 bg-white text-red-600 rounded-lg font-semibold hover:bg-gray-100 transition"
          >
            Get Started
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p>&copy; 2025 UdaipurDeals. All rights reserved.</p>
          <p className="text-gray-400 mt-2">Made with ❤️ for Udaipur's food lovers</p>
        </div>
      </footer>
    </div>
  );
}
